import noop from '@jswork/noop';
import classNames from 'classnames';
import React, { Component, HTMLAttributes } from 'react';
import ClassicEditor from '@jswork/ckeditor5-build-classic';

const CLASS_NAME = 'react-ckeditor';

export type StandardEvent = {
  target: {
    value: string;
  };
};

export type ReactCkeditorProps = {
  /**
   * Default value.
   */
  value?: string;
  /**
   * The change handler.
   */
  onChange?: (event: StandardEvent) => void;
  /**
   * The image upload adapter.
   */
  imageUploadAdapter?: any;
  /**
   * The editor options.
   */
  editorOptions?: any;
  /**
   * The adapter options.
   */
  adapterOptions?: any;
} & HTMLAttributes<HTMLDivElement>;

interface ReactCkeditorState {
  value: string;
}

export default class ReactCkeditor extends Component<ReactCkeditorProps, ReactCkeditorState> {
  static displayName = CLASS_NAME;
  static version = '__VERSION__';
  static defaultProps = {
    value: '',
    options: {},
    onChange: noop,
    imageUploadAdapter: noop,
  };

  private editor: any = null;
  private root: HTMLDivElement | null = null;

  state = {
    value: this.props.value || '',
  };

  get editorRoot() {
    return this.editor?.ui.view.element;
  }

  get rawValue() {
    return this.editor?.getData() || '';
  }

  set rawValue(value) {
    if (this.rawValue === value) return;
    this.editor?.setData(value);
    this.handleChange(value);
  }

  componentDidMount() {
    const { editorOptions, value, className } = this.props;
    const classes = classNames(CLASS_NAME, className).split(' ');
    ClassicEditor.create(this.root, { initialData: value, ...editorOptions }).then((editor) => {
      this.editor = editor;
      this.editorRoot.classList.add(...classes);
      this.onDataChange();
      this.onImageUpload();
    });
  }

  componentDidUpdate(previosProps: ReactCkeditorProps) {
    const { value } = this.props;
    if (value && value !== previosProps.value) {
      this.rawValue = value;
    }
  }

  componentWillUnmount() {
    this.editor?.destroy();
  }

  onDataChange() {
    this.editor.model.document.on('change:data', () => {
      this.handleChange(this.rawValue);
    });
  }

  handleChange = (value: string) => {
    const { onChange } = this.props;
    const target = { value };
    this.setState(target);
    onChange?.({ target });
  };

  onImageUpload() {
    const { imageUploadAdapter, adapterOptions } = this.props;
    const plugins = this.editor.plugins;
    const fileRepo = plugins.get('FileRepository');
    fileRepo.createUploadAdapter = (loader) => new imageUploadAdapter(loader, adapterOptions);
  }

  render() {
    const {
      className,
      value,
      onChange,
      editorOptions,
      adapterOptions,
      imageUploadAdapter,
      ...props
    } = this.props;

    return (
      <div
        ref={(root) => (this.root = root)}
        data-component={CLASS_NAME}
        className={classNames(`${CLASS_NAME}__root`)}
        {...props}
      />
    );
  }
}

export const ReactCkeditorFc = React.forwardRef((props: ReactCkeditorProps, ref: any) => {
  return <ReactCkeditor {...props} ref={ref} />;
});
